import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChartCard } from "./ChartCard";
import { getScoreBand } from "@/data/opportunities";

const SCORE_COLORS = {
  "High Fit (80-100)": "hsl(142, 76%, 36%)",
  "Medium Fit (60-79)": "hsl(162, 73%, 35%)",
  "Low Fit (40-59)": "hsl(38, 92%, 50%)",
  "Poor Fit (0-39)": "hsl(0, 72%, 51%)",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg px-4 py-3">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground mt-1">
          <span className="font-semibold text-foreground">{payload[0].value}</span> opportunities
        </p>
      </div>
    );
  }
  return null;
};

export const FitScoreChart = ({ data }) => {
  const chartData = useMemo(() => {
    const bands = {
      "High Fit (80-100)": 0,
      "Medium Fit (60-79)": 0,
      "Low Fit (40-59)": 0,
      "Poor Fit (0-39)": 0,
    };

    data.forEach(opp => {
      const band = getScoreBand(opp.score);
      bands[band]++;
    });

    return Object.entries(bands).map(([name, count]) => ({
      name: name.split(" (")[0],
      fullName: name,
      count,
    }));
  }, [data]);

  return (
    <ChartCard 
      title="Pipeline by Fit Score Band" 
      description="Distribution of opportunities across score ranges"
    >
      <div className="h-[280px] mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              horizontal={true}
              vertical={false}
              stroke="hsl(var(--border))"
            />
            <XAxis 
              type="number" 
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis 
              dataKey="name" 
              type="category"
              tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
            <Bar 
              dataKey="count" 
              radius={[0, 6, 6, 0]}
              maxBarSize={40}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={SCORE_COLORS[entry.fullName]} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};
